import { forwardRef } from "react";
import type { TextareaHTMLAttributes } from "react";
import { useFieldId } from "./useFieldId";
import Label from "./Label";
import { FieldError, fieldErrorId } from "./FieldError";
import { FieldHint, fieldHintId } from "./FieldHint";
import { fieldTextareaClass } from "./field-variants";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, name, ...props }, ref) => {
    const textareaId = useFieldId(id, name);
    const errorId = textareaId ? fieldErrorId(textareaId) : undefined;
    const hintId = textareaId ? fieldHintId(textareaId) : undefined;

    return (
      <div className="w-full">
        {label ? (
          <Label htmlFor={textareaId} spacing="field">{label}</Label>
        ) : null}
        <textarea
          ref={ref}
          id={textareaId}
          name={name}
          className={fieldTextareaClass(className)}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={
            error ? errorId : hint ? hintId : undefined
          }
          {...props}
        />
        {error && errorId ? (
          <FieldError id={errorId}>{error}</FieldError>
        ) : null}
        {hint && !error && hintId ? (
          <FieldHint id={hintId}>{hint}</FieldHint>
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

export default Textarea;
